import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Heart,
  ArrowLeft,
  Plus,
  GraduationCap,
  ExternalLink,
  Copy,
  Check,
  Edit,
  Trash2,
  Upload,
  Image as ImageIcon,
  Sparkles,
  TrendingUp,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB — generous so course screenshots upload cleanly
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];

const EMPTY_FORM = {
  title: '',
  creator_name: '',
  description: '',
  external_url: '',
  tags: '',
  image_data: null,
  image_type: null,
  image_preview: null,
};

export default function ContinuedEducation() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isSuperAdmin = !!(user?.is_super_admin);

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/courses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCourses(res.data || []);
    } catch (e) {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowDialog(true);
  };

  const openEdit = (course) => {
    setEditingId(course.id);
    setForm({
      title: course.title || '',
      creator_name: course.creator_name || '',
      description: course.description || '',
      external_url: course.external_url || '',
      tags: (course.tags || []).join(', '),
      image_data: null,
      image_type: null,
      image_preview: course.image_data
        ? `data:${course.image_type || 'image/png'};base64,${course.image_data}`
        : null,
    });
    setShowDialog(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Image must be under 10 MB');
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error('Use PNG, JPG, WebP or GIF');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const fullDataUrl = reader.result;
      const base64 = fullDataUrl.split(',')[1];
      setForm((f) => ({
        ...f,
        image_data: base64,
        image_type: file.type,
        image_preview: fullDataUrl,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!form.title.trim() || !form.external_url.trim()) {
      toast.error('Title and link are required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        title: form.title.trim(),
        creator_name: form.creator_name.trim(),
        description: form.description.trim(),
        external_url: form.external_url.trim(),
        tags: form.tags
          ? form.tags.split(',').map((t) => t.trim()).filter(Boolean)
          : [],
      };
      // Only send image if a new one was selected. Otherwise leave existing.
      if (form.image_data && form.image_type) {
        payload.image_data = form.image_data;
        payload.image_type = form.image_type;
      }

      if (editingId) {
        await axios.put(`${API}/courses/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Course updated');
      } else {
        await axios.post(`${API}/courses`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Course added');
      }
      setShowDialog(false);
      fetchCourses();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (course) => {
    if (!window.confirm(`Remove "${course.title}" from the course list?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/courses/${course.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Course removed');
      fetchCourses();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleCopyLink = async (course) => {
    try {
      await navigator.clipboard.writeText(course.external_url);
      setCopiedId(course.id);
      toast.success('Link copied');
      // Treat a copy as engagement too — same counter as visit.
      trackClick(course.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (e) {
      toast.error('Copy failed');
    }
  };

  const trackClick = async (courseId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/courses/${courseId}/click`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Soft refresh the counter inline so super-admin sees it bump.
      setCourses((prev) =>
        prev.map((c) =>
          c.id === courseId ? { ...c, click_count: (c.click_count || 0) + 1 } : c,
        ),
      );
    } catch (e) {
      // Silent — analytics shouldn't interrupt the user.
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              data-testid="back-btn"
              onClick={() => navigate(-1)}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-2xl font-serif text-primary-900">PlayFirst</h1>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Page header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-7 h-7 text-indigo-700" />
              </div>
              <div>
                <h2 className="text-4xl md:text-5xl font-serif font-normal text-primary-900 mb-2">
                  Continued Education
                </h2>
                <p className="text-lg text-stone-600 max-w-2xl">
                  Keep growing alongside us.
                </p>
              </div>
            </div>
            {isSuperAdmin && (
              <Button
                onClick={openAdd}
                className="rounded-full shadow-lg shadow-primary/20"
                data-testid="add-course-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add course
              </Button>
            )}
          </div>

          {/* Friendly blurb */}
          <Card
            className="p-5 mb-8 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 border border-indigo-100 rounded-3xl"
            data-testid="continued-ed-blurb"
          >
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm md:text-base text-indigo-900 leading-relaxed">
                These are some of the wonderful course creators we've collaborated with to
                broaden access to continued education for therapists. Have fun exploring —
                each link will take you to the creator's site, where you can learn more about
                their work and enrol if it speaks to you. 💛
              </p>
            </div>
          </Card>

          {/* Courses grid */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-stone-600">Loading courses…</p>
            </div>
          ) : courses.length === 0 ? (
            <Card
              className="p-12 text-center bg-white rounded-3xl border border-stone-100"
              data-testid="courses-empty-state"
            >
              <GraduationCap className="w-16 h-16 mx-auto mb-4 text-primary/40" />
              <h3 className="text-xl font-medium text-stone-700 mb-2">
                No courses listed yet
              </h3>
              <p className="text-stone-500">
                {isSuperAdmin
                  ? 'Tap "Add course" to share the first one with your team.'
                  : 'Check back soon — we\'re curating a list of trusted continued-education partners.'}
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course, idx) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.04 }}
                >
                  <Card
                    className="bg-white rounded-3xl border border-stone-100 hover:shadow-md transition-all overflow-hidden h-full flex flex-col"
                    data-testid={`course-card-${course.id}`}
                  >
                    {/* Image / placeholder — portrait 3:4 to match course poster screenshots */}
                    <div className="aspect-[3/4] w-full bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center overflow-hidden">
                      {course.image_data ? (
                        <img
                          src={`data:${course.image_type || 'image/png'};base64,${course.image_data}`}
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <ImageIcon className="w-12 h-12 text-indigo-200" />
                      )}
                    </div>

                    <div className="p-5 flex-1 flex flex-col">
                      {course.creator_name && (
                        <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">
                          {course.creator_name}
                        </p>
                      )}
                      <h3 className="text-lg font-serif text-primary-900 mb-2 line-clamp-2">
                        {course.title}
                      </h3>
                      {course.description && (
                        <p className="text-sm text-stone-600 mb-3 line-clamp-3 flex-1">
                          {course.description}
                        </p>
                      )}

                      {course.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {course.tags.slice(0, 4).map((t) => (
                            <Badge
                              key={t}
                              variant="secondary"
                              className="text-[10px] uppercase tracking-wider"
                            >
                              {t}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {isSuperAdmin && (
                        <div
                          className="flex items-center gap-1.5 text-xs text-stone-500 mb-3"
                          data-testid={`course-clicks-${course.id}`}
                        >
                          <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                          <span>
                            {course.click_count || 0} click
                            {(course.click_count || 0) === 1 ? '' : 's'}
                          </span>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex flex-wrap items-center gap-2 mt-auto pt-3 border-t border-stone-100">
                        <a
                          href={course.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackClick(course.id)}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-700 hover:text-indigo-800"
                          data-testid={`course-open-${course.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                          Visit course
                        </a>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyLink(course)}
                          className="rounded-full ml-auto"
                          data-testid={`course-copy-${course.id}`}
                        >
                          {copiedId === course.id ? (
                            <Check className="w-4 h-4 mr-1 text-emerald-600" />
                          ) : (
                            <Copy className="w-4 h-4 mr-1" />
                          )}
                          {copiedId === course.id ? 'Copied' : 'Copy link'}
                        </Button>

                        {isSuperAdmin && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEdit(course)}
                              className="rounded-full h-8 w-8 p-0"
                              data-testid={`course-edit-${course.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(course)}
                              className="rounded-full h-8 w-8 p-0 text-red-500"
                              data-testid={`course-delete-${course.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>

      {/* Add / Edit dialog (super-admin only) */}
      {isSuperAdmin && (
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent
            className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto"
            data-testid="course-dialog"
          >
            <DialogHeader>
              <DialogTitle className="text-2xl font-serif">
                {editingId ? 'Edit course' : 'Add course'}
              </DialogTitle>
              <DialogDescription>
                Featured continued-education partner. Therapists will see this on the
                Continued Education page.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSave} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="course-title">Title</Label>
                <Input
                  id="course-title"
                  data-testid="course-title-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="GLP Foundations Workshop"
                  required
                  className="h-12 rounded-2xl"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="course-creator">Creator name</Label>
                  <Input
                    id="course-creator"
                    data-testid="course-creator-input"
                    value={form.creator_name}
                    onChange={(e) => setForm({ ...form, creator_name: e.target.value })}
                    placeholder="Marge Blanc, MA, CCC-SLP"
                    className="h-12 rounded-2xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="course-url">External URL</Label>
                  <Input
                    id="course-url"
                    type="url"
                    data-testid="course-url-input"
                    value={form.external_url}
                    onChange={(e) => setForm({ ...form, external_url: e.target.value })}
                    placeholder="https://…"
                    required
                    className="h-12 rounded-2xl"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="course-desc">Description</Label>
                <Textarea
                  id="course-desc"
                  data-testid="course-description-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="A short blurb about what the course covers and who it's for…"
                  className="rounded-2xl min-h-24"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="course-tags">Tags (comma-separated)</Label>
                <Input
                  id="course-tags"
                  data-testid="course-tags-input"
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  placeholder="glp, aac, parent coaching"
                  className="h-12 rounded-2xl"
                />
              </div>

              <div className="space-y-2">
                <Label>Course image</Label>
                <div className="border-2 border-dashed border-stone-200 rounded-2xl p-4 hover:border-primary/50 transition-colors">
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                    className="hidden"
                    data-testid="course-image-input"
                  />
                  {form.image_preview ? (
                    <div className="flex flex-col items-center gap-3">
                      <img
                        src={form.image_preview}
                        alt="preview"
                        className="max-h-72 rounded-xl object-contain"
                      />
                      <p className="text-xs text-stone-500 text-center">
                        Cards display in a 3:4 portrait frame — vertical screenshots look best.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="course-image-replace-btn"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Replace image
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 py-6 text-center">
                      <Upload className="w-8 h-8 text-stone-400" />
                      <p className="text-sm text-stone-600">PNG / JPG / WebP, up to 10 MB</p>
                      <p className="text-xs text-stone-400">
                        Cards display in a 3:4 portrait frame — vertical screenshots look best.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="course-image-select-btn"
                      >
                        Select image
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="rounded-full"
                  onClick={() => setShowDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="rounded-full"
                  disabled={saving}
                  data-testid="save-course-btn"
                >
                  {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add course'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
